import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  PinInput,
  PinInputField,
  Stack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { resendOTP, verifySubmitOTP } from "../../api/authService.js";
import { fetchStatusOfTimeline } from "../../api/timelineService.js";
import useCustomToast from "../../customHooks/useCustomToast";
import { setUserDetails, setUserTimelineStep } from "../../Redux/userSlice.js";
import { extractJtiFromToken } from "../../utils/UTILITY_METHODS.js";

const VerifyPhoneNumber = ({ phoneNumber, handlePhoneChange }) => {
  const [loadingOtpSubmit, setLoadingOtpSubmit] = useState(false);
  const [resendOtpLoading, setResendOtpLoading] = useState(false);
  const [counter, setCounter] = useState(0);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const showToast = useCustomToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  //this use effect is for the timer that will run upon clicking resend otp
  useEffect(() => {
    let timer;

    if (counter > 0) {
      timer = setTimeout(() => setCounter(counter - 1), 1000);
    }

    return () => clearTimeout(timer);
  }, [counter]);

  const handleResendOTP = async () => {
    if (phoneNumber.length < 10) {
      showToast(
        "Invalid Phone Number",
        "error",
        "Phone number consists of exactly 10 digits."
      );
      return;
    }
    setResendOtpLoading(true);
    setCounter(15); //Resend otp will be available after 15 secs
    try {
      const data = await resendOTP(phoneNumber);
      if (!data[0].success) {
        throw new Error("Unable to resend OTP");
      }
      showToast(data[0].message, "success", data[0].data);
      setResendOtpLoading(false);
    } catch (error) {
      showToast(error.message, "error");
      setResendOtpLoading(false);
    }
  };

  const handleSubmitOTP = async () => {
    const fullOtp = otp.join("");

    if (phoneNumber.length < 10) {
      showToast(
        "Invalid Phone Number",
        "error",
        "Phone number consists of exactly 10 digits."
      );
      return;
    }
    if (fullOtp.length < 6) {
      showToast("Incomplete OTP", "error", "Please fill all fields");
      return;
    }
    setLoadingOtpSubmit(true);
    try {
      const data = await verifySubmitOTP(phoneNumber, fullOtp);
      const { access_token, message } = data[0].data;
      localStorage.setItem("access_token", access_token);
      showToast(message, "success");
      setLoadingOtpSubmit(false);
      dispatch(setUserDetails({ userPhone: phoneNumber }));

      const jti = extractJtiFromToken(access_token);
      // Replace the browser_uuid with the jti
      if (jti) {
        localStorage.setItem("browser_uuid", jti);
      }

      const timelineResponse = await fetchStatusOfTimeline();
      const { current_step } = timelineResponse.data.status;

      if (current_step >= 5) {
        navigate("/documents");
      } else {
        navigate("/user");
      }
      dispatch(setUserTimelineStep(current_step));
    } catch (error) {
      showToast(error.message, "error");
      setLoadingOtpSubmit(false);
    }
  };

  const handleFillingOTPChange = (value, index) => {
    if (otp[otp.length - 1].length !== 0) return;
    const newOtp = [...otp];
    newOtp[index] = value; // Update the specific digit
    setOtp(newOtp);
  };

  return (
    <form>
      <Stack spacing={6}>
        <FormControl isRequired>
          <FormLabel htmlFor="phone">Phone Number</FormLabel>
          <Input
            id="phone"
            type="tel"
            placeholder="Enter your phone number"
            name="phoneNumber"
            value={phoneNumber}
            onChange={handlePhoneChange}
            maxLength={10}
            focusBorderColor="teal.500"
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>OTP</FormLabel>
          <HStack>
            <PinInput>
              {otp.map((value, index) => (
                <PinInputField
                  key={index}
                  id={`otp-${index + 1}-digit`}
                  value={value}
                  onChange={(e) =>
                    handleFillingOTPChange(e.target.value, index)
                  }
                />
              ))}
            </PinInput>
          </HStack>
        </FormControl>

        <Stack direction="row" spacing={4} align="center" className="mt-3">
          <Button
            onClick={handleSubmitOTP}
            type="button"
            colorScheme="teal"
            isLoading={loadingOtpSubmit}
            loadingText="Submitting OTP"
            size="md"
            px={10}
            width={{ base: "full", sm: "auto" }}
            alignSelf="center"
          >
            Submit OTP
          </Button>
          <Button
            onClick={handleResendOTP}
            type="button"
            colorScheme="teal"
            isLoading={resendOtpLoading || counter > 0}
            loadingText={
              resendOtpLoading ? "Resending OTP" : `Resend OTP (${counter}s)`
            }
            size="md"
            px={10}
            width={{ base: "full", sm: "auto" }}
            alignSelf="center"
          >
            Resend OTP
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};

export default VerifyPhoneNumber;
